import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  SubheaderBar,
  SubheaderLeft,
  SubheaderRight,
  BackButton,
  CurrentSectionIndicator,
  SectionNumberBadge,
  SectionNameText,
  SubheaderButton,
  ProgressBar,
} from "@dd-report-components";
import { FiArrowLeft, FiDownload, FiLoader } from "react-icons/fi";
import { DueDiligenceTier } from "../../../../Common/Types-DTOs/DueDiligence";
import { ReportTierControl } from "./ReportTierControl";

interface SectionInfo {
  number: number;
  title: string;
  icon: string;
}

interface Props {
  currentSection: SectionInfo;
  totalSections: number;
  onExport?: () => void;
  isGenerating?: boolean;
  tier?: DueDiligenceTier | null;
  pricing?: { basic: number | null; professional: number | null; premium: number | null } | null;
  onUpgrade?: (tier: DueDiligenceTier) => void;
  /** Prices the upgrades in credits: subscribers pay them with credits. */
  creditCosts?: Record<DueDiligenceTier, number> | null;
  // MIKE-232: the report can be read in another language.
  language?: string | null;
  onLanguageChange?: (language: string | null) => void;
}

// Fixed pilot list — no shared-types package between backend/frontend yet
// (MIKE-266), so this mirrors backend/src/App/DueDiligence/TranslateFinalReportBlob.ts's
// LANGUAGE_NAMES by hand. Keep both in sync when adding a language.
const SUPPORTED_TRANSLATION_LANGUAGES = [
  { value: "en", label: "English" },
  { value: "ca", label: "Català" },
];

const LanguageSelect = styled.select`
  height: 2rem;
  padding: 0 1.75rem 0 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  background-color: #ffffff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%236B7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.65rem center;
  background-size: 0.65rem;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #0d9488;
    box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.15);
  }
`;

export const ReportSubheader = ({
  currentSection,
  totalSections,
  onExport,
  isGenerating,
  tier,
  pricing,
  onUpgrade,
  creditCosts,
  language,
  onLanguageChange,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { caseAlias } = useParams<{ caseAlias: string }>();

  const progress = totalSections > 0 ? (currentSection.number / totalSections) * 100 : 0;

  return (
    <SubheaderBar data-subheader>
      <SubheaderLeft>
        <BackButton onClick={() => navigate(`/due-diligence/case/${caseAlias}`)}>
          <FiArrowLeft size={16} />
          {t("dueDiligence.legalFiscalReport.reportSubheader.backButton")}
        </BackButton>

        <CurrentSectionIndicator>
          <SectionNumberBadge>
            {currentSection.icon} {currentSection.number}
          </SectionNumberBadge>
          <SectionNameText>{currentSection.title}</SectionNameText>
        </CurrentSectionIndicator>
      </SubheaderLeft>

      <SubheaderRight>
        {onLanguageChange && (
          <LanguageSelect
            value={language ?? ""}
            onChange={(e) => onLanguageChange(e.target.value || null)}
          >
            <option value="">{t("dueDiligence.legalFiscalReport.reportSubheader.languageOriginal")}</option>
            {SUPPORTED_TRANSLATION_LANGUAGES.map((lang) => (
              <option key={lang.value} value={lang.value}>
                {lang.label}
              </option>
            ))}
          </LanguageSelect>
        )}
        {tier && (
          <ReportTierControl currentTier={tier} pricing={pricing} creditCosts={creditCosts} onUpgrade={onUpgrade} />
        )}
        {isGenerating ? (
          <SubheaderButton as="div" style={{ cursor: "default", opacity: 0.8 }}>
            <FiLoader size={14} style={{ animation: "spin 1.5s linear infinite" }} />
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            <span>{t("dueDiligence.legalFiscalReport.reportSubheader.generatingButton")}</span>
          </SubheaderButton>
        ) : onExport ? (
          <SubheaderButton onClick={onExport}>
            <FiDownload size={14} />
            <span>{t("dueDiligence.legalFiscalReport.reportSubheader.exportButton")}</span>
          </SubheaderButton>
        ) : null}
      </SubheaderRight>

      <ProgressBar style={{ width: `${progress}%` }} />
    </SubheaderBar>
  );
};
